'use client';

import { GTM_WORKFLOW } from '@/config/dashboardWorkflows';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useDashboardDragDrop } from '@/hooks/useDashboardDragDrop';
import { GTMCard, mapCardTypeToNumber, mapToGTMCard } from '@/utils/cardMappers';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import DashboardColumn from './DashboardColumn';
import StarsBackground from './StarsBackground';
import TaskModal from './TaskModal';

export default function GTMDashboard() {
  const queryClient = useQueryClient();
  const { data: tasksData, isLoading, error } = useDashboardData('gtm');
  const { handleCardDrop: onCardDrop, handleCardDragStart: onCardDragStart } = useDashboardDragDrop('gtm');
  
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | undefined>(undefined);
  const [taskModalStage, setTaskModalStage] = useState<number | undefined>(undefined);
  const [taskModalCardType, setTaskModalCardType] = useState<number | null>(null);
  const [showAddContentDropdown, setShowAddContentDropdown] = useState(false);
  const [selectedCardTypeFilter, setSelectedCardTypeFilter] = useState<number | null>(null); // null = all types

  // Map API data to GTM cards with memoization
  const allTasks = useMemo(() => {
    if (!tasksData) return [];
    return tasksData.map(mapToGTMCard);
  }, [tasksData]);

  // Filter tasks by selected card type
  const tasks = useMemo(() => {
    if (selectedCardTypeFilter === null) {
      return allTasks;
    }
    return allTasks.filter((task: GTMCard) => {
      const taskCardTypeNum = task.cardTypeNum ?? mapCardTypeToNumber(task.type);
      return taskCardTypeNum === selectedCardTypeFilter;
    });
  }, [allTasks, selectedCardTypeFilter]);

  // Calculate KPIs with memoization (based on filtered tasks)
  const kpis = useMemo(() => {
    const totalItems = tasks.length;
    const publishedCount = tasks.filter((t: GTMCard) => t.stage === 4).length;
    const inReviewCount = tasks.filter((t: GTMCard) => t.stage === 2).length;
    const scheduledCount = tasks.filter((t: GTMCard) => t.stage === 3).length;
    return { totalItems, publishedCount, inReviewCount, scheduledCount };
  }, [tasks]);

  const handleCardClick = (taskId: string) => {
    const task = tasks.find((t: GTMCard) => t.taskId === taskId);
    if (task && task.taskIdNum) {
      const cardTypeNum = task.cardTypeNum ?? mapCardTypeToNumber(task.type);
      setEditingTaskId(task.taskIdNum);
      setTaskModalStage(task.stage);
      setTaskModalCardType(cardTypeNum);
      setIsTaskModalOpen(true);
    }
  };

  const handleAddContent = (cardType: number) => {
    setEditingTaskId(undefined);
    setTaskModalCardType(cardType);
    setTaskModalStage(0);
    setIsTaskModalOpen(true);
    setShowAddContentDropdown(false);
  };

  const handleTaskSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'gtm'] });
    setIsTaskModalOpen(false);
    setEditingTaskId(undefined);
    setTaskModalStage(undefined);
    setTaskModalCardType(null);
  };

  const handleEditContent = (taskId: number) => {
    const task = tasks.find((t: GTMCard) => t.taskIdNum === taskId);
    if (task?.dashboardData?.google_docs_link) {
      window.open(task.dashboardData.google_docs_link.trim(), '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardDrop = (e: React.DragEvent, taskId: string, newStage: number) => {
    onCardDrop(e, taskId, newStage, tasks);
  };

  const handleCardDragStart = (e: React.DragEvent, taskId: string) => {
    const task = tasks.find((t: GTMCard) => t.taskId === taskId);
    if (task) {
      onCardDragStart(e, taskId, task.stage);
    }
  };

  // Group tasks by stage with memoization
  const tasksByStage = useMemo(() => {
    return GTM_WORKFLOW.stages.map(stage => ({
      ...stage,
      cards: tasks
        .filter((t: GTMCard) => t.stage === stage.stage)
        .map((card: GTMCard) => ({
          id: card.id,
          title: card.title,
          type: card.type,
          taskId: card.taskId,
          taskIdNum: card.taskIdNum,
          assignedTo: card.assignedTo,
          dashboardData: card.dashboardData,
        })),
    }));
  }, [tasks]);

  // Card type filter configuration
  const cardTypeFilters = [
    { type: 1, label: 'Whitepaper', icon: '📄' },
    { type: 2, label: 'Blog', icon: '✍️' },
    { type: 3, label: 'Podcast', icon: '🎙️' },
    { type: 4, label: 'Landing Page', icon: '🚀' },
    { type: 5, label: 'Email Campaign', icon: '📧' },
  ];

  return (
    <div className="flex-1 overflow-hidden flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <StarsBackground />

      {/* Dashboard Header Bar with KPIs */}
      <div className="relative z-50 flex items-center flex-nowrap gap-6 mb-4 p-5 bg-slate-800/40 backdrop-blur-sm border-b border-slate-700/50">
        {/* Dashboard Title */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-2xl">📣</span>
          <h1 className="text-xl font-semibold text-white">GTM Dashboard</h1>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-slate-600 flex-shrink-0"></div>

        {/* KPIs */}
        <div className="flex items-center gap-6 flex-shrink min-w-0 flex-nowrap">
          <div className="text-center">
            <div className="text-2xl font-bold text-white">{kpis.totalItems}</div>
            <div className="text-xs text-slate-400">Total Items</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-400">{kpis.publishedCount}</div>
            <div className="text-xs text-slate-400">Published (Dec)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{kpis.inReviewCount}</div>
            <div className="text-xs text-slate-400">In Review</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-400">{kpis.scheduledCount}</div>
            <div className="text-xs text-slate-400">Scheduled</div>
          </div>
        </div>

        {/* Filters - Right Side */}
        <div className="flex items-end gap-3 ml-auto flex-shrink-0">
          {/* Member Filter */}
          <div>
            <label className="text-slate-500 text-xs block mb-1">Member:</label>
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[140px]">
              <div className="w-5 h-5 rounded-full bg-slate-600 flex items-center justify-center">
                <svg className="w-3 h-3 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-slate-300 text-sm flex-1">All Members</span>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <label className="text-slate-500 text-xs block mb-1">Type:</label>
            <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded px-3 py-1.5 min-w-[120px]">
              <span className="text-slate-300 text-sm flex-1">All Types</span>
              <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Add Content Button with Dropdown */}
          <div className="relative z-[60]">
            <button 
              onClick={() => setShowAddContentDropdown(!showAddContentDropdown)}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Content
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showAddContentDropdown && (
              <>
                <div className="fixed inset-0 z-[55]" onClick={() => setShowAddContentDropdown(false)} />
                <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-[60] py-1 min-w-[180px]">
                  <button
                    onClick={() => handleAddContent(1)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                  >
                    📄 Whitepaper
                  </button>
                  <button
                    onClick={() => handleAddContent(2)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                  >
                    ✍️ Blog
                  </button>
                  <button
                    onClick={() => handleAddContent(3)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                  >
                    🎙️ Podcast
                  </button>
                  <button
                    onClick={() => handleAddContent(4)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                  >
                    🚀 Landing Page
                  </button>
                  <button
                    onClick={() => handleAddContent(5)}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-slate-700 transition-colors text-white"
                  >
                    📧 Email Campaign
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="relative z-10 flex-1 flex overflow-hidden">
        {/* Sidebar with Content Type Filters */}
        <div className="w-64 bg-slate-800/60 backdrop-blur-sm border-r border-slate-700/50 flex flex-col">
          <div className="p-4 border-b border-slate-700/50">
            <h2 className="text-sm font-semibold text-slate-300 mb-3">Content Types</h2>
            <button
              onClick={() => setSelectedCardTypeFilter(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-2 ${
                selectedCardTypeFilter === null
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/70'
              }`}
            >
              <span>All Types</span>
            </button>
            {cardTypeFilters.map((filter) => (
              <button
                key={filter.type}
                onClick={() => setSelectedCardTypeFilter(filter.type)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mb-2 ${
                  selectedCardTypeFilter === filter.type
                    ? 'bg-slate-700 text-white'
                    : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700/70'
                }`}
              >
                <span>{filter.icon}</span>
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Workflow view */}
        <div className="relative z-10 flex-1 overflow-x-auto pb-4 px-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400 text-sm">Loading dashboard tasks...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-400 text-sm">{error instanceof Error ? error.message : 'Failed to load tasks'}</div>
          </div>
        ) : (
          <div className="flex gap-4 min-w-max">
            {tasksByStage.map((column) => (
              <DashboardColumn
                key={column.stage}
                title={column.label}
                headerColor={column.color as 'slate' | 'blue' | 'purple' | 'amber' | 'emerald'}
                stage={column.stage}
                cards={column.cards}
                onCardClick={handleCardClick}
                onCardDrop={(e, taskId) => handleCardDrop(e, taskId, column.stage)}
                onCardDragStart={handleCardDragStart}
                onEditContent={handleEditContent}
              />
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => {
          setIsTaskModalOpen(false);
          setEditingTaskId(undefined);
          setTaskModalStage(undefined);
          setTaskModalCardType(null);
        }}
        mode={editingTaskId ? 'edit' : 'create'}
        taskId={editingTaskId}
        dashboardType="gtm"
        dashboardStage={taskModalStage}
        dashboardCardType={taskModalCardType}
        onTaskSaved={handleTaskSaved}
      />

    </div>
  );
}

